import { useTheme } from '../contexts/ThemeContext';

interface CampaignTypeModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSelectWhatsWeb: () => void;
  onSelectOfficialApi: () => void;
  canUseWhatsWeb?: boolean;
  canUseOfficialApi?: boolean;
}

export function CampaignTypeModal({ 
  isOpen, 
  onClose, 
  onSelectWhatsWeb, 
  onSelectOfficialApi,
  canUseWhatsWeb = true,
  canUseOfficialApi = true
}: CampaignTypeModalProps) {
  const { theme } = useTheme();

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[9999] p-4">
      <div 
        className="rounded-lg shadow-2xl max-w-4xl w-full p-8 transform transition-all"
        style={{ 
          backgroundColor: theme === 'dark' ? '#1D1E24' : '#ffffff',
        }}
      >
        {/* Header */}
        <div className="text-center mb-8">
          <h2 className="text-xl font-semibold mb-2 text-n-slate-12">
            Escolha o Tipo de Envio
          </h2>
          <p className={`text-lg ${theme === 'dark' ? 'text-gray-300' : 'text-gray-600'}`}>
            Selecione como deseja enviar sua campanha
          </p>
        </div>

        {/* Options Grid */}
        <div className={`grid grid-cols-1 ${canUseWhatsWeb && canUseOfficialApi ? 'md:grid-cols-2' : ''} gap-6 mb-8`}>
          {/* WhatsApp Web Option */}
          {canUseWhatsWeb && (
          <button
            onClick={onSelectWhatsWeb}
            className="group relative p-8 rounded-xl border-2 transition-all duration-300 hover:scale-105 hover:shadow-xl"
            style={{
              backgroundColor: theme === 'dark' ? '#3d3d40' : '#f8fafc',
              borderColor: theme === 'dark' ? '#4d4d50' : '#e2e8f0',
            }}
          >
            <div className="text-center">
              {/* Icon */}
              <div className="mb-4 flex justify-center">
                <div 
                  className="w-20 h-20 rounded-full flex items-center justify-center transition-transform group-hover:scale-110"
                  style={{ backgroundColor: '#25D366' }}
                >
                  <span className="text-5xl">📱</span>
                </div>
              </div>

              {/* Title */}
              <h3 className="text-xl font-semibold mb-3 text-n-slate-12">
                WhatsApp Web
              </h3>

              {/* Description */}
              <p className={`text-sm mb-4 ${theme === 'dark' ? 'text-gray-300' : 'text-gray-600'}`}>
                Use suas sessões conectadas no WhatsApp Web para enviar mensagens
              </p>

              {/* Features */}
              <div className="space-y-2 text-left">
                <div className="flex items-start">
                  <span className="text-green-500 mr-2">✓</span>
                  <span className={`text-sm ${theme === 'dark' ? 'text-gray-300' : 'text-gray-600'}`}>
                    Múltiplas sessões simultâneas
                  </span>
                </div>
                <div className="flex items-start">
                  <span className="text-green-500 mr-2">✓</span>
                  <span className={`text-sm ${theme === 'dark' ? 'text-gray-300' : 'text-gray-600'}`}>
                    Mensagens personalizadas
                  </span>
                </div>
                <div className="flex items-start">
                  <span className="text-green-500 mr-2">✓</span>
                  <span className={`text-sm ${theme === 'dark' ? 'text-gray-300' : 'text-gray-600'}`}>
                    Suporte a mídias diversas
                  </span>
                </div>
                <div className="flex items-start">
                  <span className="text-green-500 mr-2">✓</span>
                  <span className={`text-sm ${theme === 'dark' ? 'text-gray-300' : 'text-gray-600'}`}>
                    Variações de mensagens
                  </span>
                </div>
              </div>

              {/* Button */}
              <div className="mt-6">
                <div 
                  className="py-3 px-6 rounded-lg font-semibold text-white transition-colors"
                  style={{ backgroundColor: '#25D366' }}
                >
                  Selecionar WhatsApp Web
                </div>
              </div>
            </div>

            {/* Badge */}
            <div className="absolute top-4 right-4">
              <span className="bg-green-500 text-white text-xs font-bold px-3 py-1 rounded-full">
                Recomendado
              </span>
            </div>
          </button>
          )}

          {/* Official API Option */}
          {canUseOfficialApi && (
          <button
            onClick={onSelectOfficialApi}
            className="group relative p-8 rounded-xl border-2 transition-all duration-300 hover:scale-105 hover:shadow-xl"
            style={{
              backgroundColor: theme === 'dark' ? '#3d3d40' : '#f8fafc',
              borderColor: theme === 'dark' ? '#4d4d50' : '#e2e8f0',
            }}
          >
            <div className="text-center">
              {/* Icon */}
              <div className="mb-4 flex justify-center">
                <div 
                  className="w-20 h-20 rounded-full flex items-center justify-center transition-transform group-hover:scale-110"
                  style={{ backgroundColor: 'var(--primary-button)' }}
                >
                  <span className="text-5xl">✅</span>
                </div>
              </div>

              {/* Title */}
              <h3 className="text-xl font-semibold mb-3 text-n-slate-12">
                API Oficial WhatsApp
              </h3>

              {/* Description */}
              <p className={`text-sm mb-4 ${theme === 'dark' ? 'text-gray-300' : 'text-gray-600'}`}>
                Use a API oficial do WhatsApp Business para envios em larga escala
              </p>

              {/* Features */}
              <div className="space-y-2 text-left">
                <div className="flex items-start">
                  <span className="text-blue-500 mr-2">✓</span>
                  <span className={`text-sm ${theme === 'dark' ? 'text-gray-300' : 'text-gray-600'}`}>
                    Alta taxa de entrega
                  </span>
                </div>
                <div className="flex items-start">
                  <span className="text-blue-500 mr-2">✓</span>
                  <span className={`text-sm ${theme === 'dark' ? 'text-gray-300' : 'text-gray-600'}`}>
                    Templates aprovados
                  </span>
                </div>
                <div className="flex items-start">
                  <span className="text-blue-500 mr-2">✓</span>
                  <span className={`text-sm ${theme === 'dark' ? 'text-gray-300' : 'text-gray-600'}`}>
                    Escalabilidade empresarial
                  </span>
                </div>
                <div className="flex items-start">
                  <span className="text-blue-500 mr-2">✓</span>
                  <span className={`text-sm ${theme === 'dark' ? 'text-gray-300' : 'text-gray-600'}`}>
                    Relatórios avançados
                  </span>
                </div>
              </div>

              {/* Button */}
              <div className="mt-6">
                <div 
                  className="py-3 px-6 rounded-lg font-semibold text-white transition-colors"
                  style={{ backgroundColor: 'var(--primary-button)' }}
                >
                  Selecionar API Oficial
                </div>
              </div>
            </div>

            {/* Badge */}
            <div className="absolute top-4 right-4">
              <span className="bg-blue-500 text-white text-xs font-bold px-3 py-1 rounded-full">
                Enterprise
              </span>
            </div>
          </button>
          )}
        </div>

        {/* Footer */}
        <div className="flex justify-center">
          <button
            onClick={onClose}
            className={`px-6 py-3 rounded-lg font-medium transition-colors ${
              theme === 'dark' 
                ? 'bg-gray-700 text-gray-300 hover:bg-gray-600' 
                : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
            }`}
          >
            Cancelar
          </button>
        </div>
      </div>
    </div>
  );
}

